import React, { useState, useEffect, useRef } from 'react';
import { X, ClipboardPlus, Database, ScrollText, Bug, ChevronUp, ChevronDown } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { devFilesApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import { useDatabaseSafe } from '../../context/DatabaseContext';
import { useAuth } from '../../context/AuthContext';
import { usePreferences } from '../../context/PreferencesContext';
import {
  DEBUG_HOVER_TOOLS,
  DEBUG_OVERLAY_TOOLS,
  CSS_VARS_MODE_KEY,
  isDebugPanelVisible,
  syncHoverState,
  syncOverlayTool,
  notifyDebugChanged,
  refreshTabOrder,
} from '../../utils/debugOverlay';

const THEME_CLASSES = ['dawn-theme-dark', 'dawn-theme-dim', 'dawn-theme-sepia', 'dawn-theme-hc', 'dawn-theme-t1e', 'dawn-theme-buckeye', 'dawn-theme-irish', 'dawn-theme-mariners'];
const THEME_OPTIONS = [
  { label: 'Standard', value: 'standard'  },
  { label: 'Dark',     value: 'dark'      },
  { label: 'Dim',      value: 'dim'       },
  { label: 'Sepia',    value: 'sepia'     },
  { label: 'Hi-Con',   value: 'hc'        },
  { label: 'T1E',      value: 't1e'       },
  { label: 'Buckeye',  value: 'buckeye'   },
  { label: 'Irish',    value: 'irish'     },
  { label: 'Mariners', value: 'mariners'  },
];
const applyTheme = (value) => {
  document.documentElement.classList.remove(...THEME_CLASSES);
  if (value && value !== 'standard') document.documentElement.classList.add(`dawn-theme-${value}`);
};

// Development pages that show the help icon
const DEV_PAGES = ['development-dashboard', 'task-log', 'rev-log', 'dev-plan', 'schema-viewer', 'claude-notes', 'sample-table-view', 'standard-view-config'];

// Help content for each dev page - structured with sections
const PAGE_HELP = {
  'development-dashboard': {
    title: 'Development Dashboard',
    sections: [
      {
        heading: 'Overview',
        items: [
          'Central hub for all development tools',
          'Visual summary of each tool with donut charts',
          'Click any tile to navigate to that page'
        ]
      },
      {
        heading: 'Tiles',
        items: [
          'Current Sprint - Task statuses donut (Pending/In Progress/Validate/Complete)',
          'Dev Plan - Step statuses donut (Not Started/In Progress/Complete)',
          'Rev Log - Sparkline showing minor revs, vertical lines at major versions',
          'Schema Viewer - Badge counts for tables and columns',
          'Navigation - Active vs future nav items donut',
          'Module Templates - Configured vs unconfigured tables donut'
        ]
      },
      {
        heading: 'Buttons',
        items: [
          'Refresh - Reload all dashboard statistics'
        ]
      }
    ]
  },
  'task-log': {
    title: 'Task Log',
    sections: [
      {
        heading: 'Overview',
        items: [
          'Task management for development work',
          'Tasks move through status workflow from entry to completion'
        ]
      },
      {
        heading: 'Status Flow',
        items: [
          'Pending (yellow pulse) - Waiting to be worked on',
          'In Progress (yellow solid) - Claude is actively working',
          'Validate (blue) - Work done, awaiting your review',
          'Complete (green) - Accepted and finished'
        ]
      },
      {
        heading: 'Buttons',
        items: [
          'Plus (+) - Add new task',
          'Eye/EyeOff - Toggle completed task visibility',
          'Refresh - Reload from file',
          'Pencil (pending only) - Edit task',
          'Trash (pending only) - Delete task',
          'Eye (validate/complete) - View original request'
        ]
      },
      {
        heading: 'Steps',
        items: [
          '1. Add task via + button or footer quick-add',
          '2. Claude picks up pending tasks and marks In Progress',
          '3. When done, Claude marks Validate for your review',
          '4. Use dropdown: Accept (complete) or Retry (with notes)',
          '5. Double-click Complete tasks to re-open if needed'
        ]
      }
    ]
  },
  'rev-log': {
    title: 'Revision Log',
    sections: [
      {
        heading: 'Overview',
        items: [
          'Version history and release notes',
          'Format: vX.YYY (X=major, YYY=minor)'
        ]
      },
      {
        heading: 'Badge Key',
        items: [
          'MAJ (orange) - Major version: dev plan step completed',
          'MIN (blue) - Minor version: sprint task completed'
        ]
      },
      {
        heading: 'Buttons',
        items: [
          'Plus (+) - Add new revision',
          'Pencil - Edit selected revision',
          'Save - Save changes',
          'X - Cancel editing',
          'Trash - Delete revision',
          'Refresh - Reload from file'
        ]
      },
      {
        heading: 'Steps',
        items: [
          '1. Select a revision from the list to view details',
          '2. Click Pencil to edit summary or changes',
          '3. Add change entries with + Add Change button',
          '4. Save to persist or X to cancel'
        ]
      }
    ]
  },
  'dev-plan': {
    title: 'Development Plan',
    sections: [
      {
        heading: 'Overview',
        items: [
          '19-step roadmap from foundation to production',
          'Steps 1-4: Foundation (complete)',
          'Steps 5-6: Dev Tools UI, Config Editors',
          'Steps 7-11: Dynamic modules, CRUD, Security',
          'Steps 12+: Auth, APIs, advanced features'
        ]
      },
      {
        heading: 'Status Key',
        items: [
          'Check (green) - Complete',
          'Clock (yellow) - In Progress',
          'Circle (gray) - Not Started'
        ]
      },
      {
        heading: 'Buttons',
        items: [
          'Plus (+) - Add new step',
          'Pencil - Edit selected step',
          'Save - Save changes',
          'X - Cancel editing',
          'Trash - Delete step',
          'Refresh - Reload from file',
          'Up/Down arrows (detail) - Reorder step position'
        ]
      },
      {
        heading: 'Steps',
        items: [
          '1. Select step from list to view details',
          '2. Click Pencil to enter edit mode',
          '3. Change name, status, description, or notes',
          '4. Click step number to reassign position (non-complete only)',
          '5. Save to persist or X to cancel'
        ]
      }
    ]
  },
  'schema-viewer': {
    title: 'Database Schema',
    sections: [
      {
        heading: 'Overview',
        items: [
          'Live view of database structure from INFORMATION_SCHEMA',
          'Shows all tables with columns, types, and relationships'
        ]
      },
      {
        heading: 'Column Info',
        items: [
          'Table - Source table name',
          'Column - Field name',
          'Data Type - SQL data type',
          'Nullable - NULL or NOT NULL',
          'PK - Primary key indicator',
          'Identity - Auto-increment field',
          'Foreign Key - FK relationship target'
        ]
      },
      {
        heading: 'Buttons',
        items: [
          'Refresh - Re-query database schema'
        ]
      },
      {
        heading: 'Features',
        items: [
          'Click column headers to sort',
          'Use column dropdowns to filter by specific values',
          'Active filters shown above grid with clear option',
          'Future: Will evolve into Security Manager (Step 11)'
        ]
      }
    ]
  },
  'claude-notes': {
    title: 'PMD Claude Notes',
    sections: [
      {
        heading: 'Overview',
        items: [
          'PMD app-specific documentation for Claude Code sessions',
          'Synced with dev-tracking/PMD-Claude-Notes.md file',
          'Contains architecture, patterns, configs, and session history'
        ]
      },
      {
        heading: 'File Organization',
        items: [
          'CLAUDE.md - People, git rules, approvals, communication style',
          'PMD-Claude-Notes.md - App architecture, patterns, session notes'
        ]
      },
      {
        heading: 'Buttons',
        items: [
          'Plus (+) - Add timestamped note to Notes section',
          'Refresh - Reload from file'
        ]
      },
      {
        heading: 'Content Sections',
        items: [
          'Quick Start - Session initialization steps',
          'Key Files - Important project files and their purpose',
          'Versioning - Version format and update locations',
          'Development Pages - Available dev tools',
          'Module Config - JSON config structure',
          'Session Notes - Daily work log'
        ]
      }
    ]
  },
  'sample-table-view': {
    title: 'Sample Table View',
    sections: [
      {
        heading: 'Overview',
        items: [
          'Reference implementation for list/detail modules',
          'Demonstrates standard patterns and layouts'
        ]
      },
      {
        heading: 'Components',
        items: [
          'ListPanel - Left-side scrollable item list',
          'Detail Form - Right-side 24-column grid layout',
          'ModuleHeader - View toggle, search, CRUD buttons'
        ]
      },
      {
        heading: 'Usage',
        items: [
          'Use as template when building new table modules',
          'Copy patterns for consistent UI/UX across modules'
        ]
      }
    ]
  },
  'navigation': {
    title: 'Navigation Editor',
    sections: [
      {
        heading: 'Overview',
        items: [
          'Visual editor for sidebar navigation structure',
          'Changes update navigationConfig.js directly'
        ]
      },
      {
        heading: 'Badge Key',
        items: [
          'Future (gray) - Under construction / not built yet',
          'Admin (red) - Only visible to admins',
          'New (green) - Newly added item'
        ]
      },
      {
        heading: 'Buttons (Header)',
        items: [
          'Plus (+) - Add root-level item',
          'Save - Apply changes (creates sprint task)',
          'X - Cancel all changes',
          'Refresh - Reset to current config'
        ]
      },
      {
        heading: 'Row Controls',
        items: [
          'Up/Down arrows - Reorder within siblings',
          'Left arrow - Outdent (move up a level)',
          'Right arrow - Indent (make child of previous)',
          'Plus (+) - Add child item',
          'Trash - Delete item and children'
        ]
      },
      {
        heading: 'Steps',
        items: [
          '1. Expand/collapse items with chevron',
          '2. Use arrows to reorder or change nesting',
          '3. Add new items at root or as children',
          '4. Click Save when done - changes are live'
        ]
      }
    ]
  },
  'standard-view-config': {
    title: 'Standard View Config',
    sections: [
      {
        heading: 'Overview',
        items: [
          'Visual form builder for module configurations',
          'Creates JSON config files in module-configs/'
        ]
      },
      {
        heading: 'Table Icons',
        items: [
          'Green check - Has config file',
          'Blue server - Has API controller',
          'No icons - Needs config and/or API'
        ]
      },
      {
        heading: 'Buttons',
        items: [
          'Init from DB - Create config from database schema',
          'Save - Write JSON config to file',
          'Create Task - Add module build task to sprint'
        ]
      },
      {
        heading: 'Canvas',
        items: [
          'Drag fields from palette to 24-column grid',
          'Click row Add (+) to insert new row',
          'Double-click or gear icon to edit properties',
          'Use Add Section to create field groupings',
          'X button removes field (returns to palette)'
        ]
      },
      {
        heading: 'Steps',
        items: [
          '1. Select table from dropdown',
          '2. Click Init from DB (or load existing)',
          '3. Arrange fields on canvas by dragging',
          '4. Configure field properties (label, colspan, type)',
          '5. Set List Panel options (primary/secondary line)',
          '6. Save config, then Create Task if needed'
        ]
      }
    ]
  }
};

/**
 * Footer component with Add Task functionality for current sprint (admin only)
 */
const Footer = ({ currentPage, isAdmin }) => {
  // isSysAdmin from AuthContext — the Add Task button posts to a
  // SysAdmin-only endpoint, so hide it for AppAdmins to prevent silent
  // 403s (a20.046). The isAdmin prop is retained for other conditionals.
  const { isSysAdmin } = useAuth();
  const location = useLocation();
  const [showAddTask, setShowAddTask] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newTask, setNewTask] = useState({ task: '', details: '' });

  // Debug panel state
  const allTools = [...DEBUG_HOVER_TOOLS, ...DEBUG_OVERLAY_TOOLS];
  const readToolStates = () => {
    const out = {};
    allTools.forEach(t => { out[t.lsKey] = localStorage.getItem(t.lsKey) === 'true'; });
    return out;
  };
  const [toolStates,   setToolStates]   = useState(readToolStates);
  const [debugActive,  setDebugActive]  = useState(isDebugPanelVisible);
  const [debugOpen,    setDebugOpen]    = useState(false);
  const [cssVarsMode,  setCssVarsMode]  = useState(() => localStorage.getItem(CSS_VARS_MODE_KEY) || 'used');
  const [theme,        setTheme]        = useState(() => localStorage.getItem('dawn.uiTheme') || 'standard');
  const debugPanelRef = useRef(null);

  // Restore hover listeners and overlay tools on page load/refresh
  useEffect(() => {
    syncHoverState();
    DEBUG_OVERLAY_TOOLS.forEach(t => {
      if (localStorage.getItem(t.lsKey) === 'true') syncOverlayTool(t.key, true);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync when tools change from Preferences page
  useEffect(() => {
    const handler = () => {
      setToolStates(readToolStates());
      setDebugActive(isDebugPanelVisible());
      setCssVarsMode(localStorage.getItem(CSS_VARS_MODE_KEY) || 'used');
    };
    window.addEventListener('dawn-debug-changed', handler);
    return () => window.removeEventListener('dawn-debug-changed', handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync theme when changed from Preferences page
  useEffect(() => {
    const handler = () => setTheme(localStorage.getItem('dawn.uiTheme') || 'standard');
    window.addEventListener('dawn-theme-changed', handler);
    return () => window.removeEventListener('dawn-theme-changed', handler);
  }, []);

  // Shift+F12 opens/closes panel from any page
  useEffect(() => {
    const handler = (e) => {
      if (e.shiftKey && e.key === 'F12') {
        e.preventDefault();
        if (isDebugPanelVisible()) setDebugOpen(o => !o);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Refresh tab order numbers when navigating to a new page
  useEffect(() => {
    const timer = setTimeout(refreshTabOrder, 150);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Close panel on outside click
  useEffect(() => {
    if (!debugOpen) return;
    const handler = (e) => {
      if (debugPanelRef.current && !debugPanelRef.current.contains(e.target)) setDebugOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [debugOpen]);

  // Server-backed preference write path (mirrors + debounced persistence).
  // Quick toggles stay immediate-write — no dirty/save ceremony down here.
  const { setPref, rows: prefRows, slugToDbId } = usePreferences();

  const handleDebugToggle = (key, lsKey, type, checked) => {
    setPref(`debug.${type}.${key}`, checked ? 'true' : 'false');   // writes mirror + persists
    setToolStates(prev => ({ ...prev, [lsKey]: checked }));
    if (type === 'hover') syncHoverState();
    else                  syncOverlayTool(key, checked);
    notifyDebugChanged();
  };

  const handleCssVarsMode = (value) => {
    setCssVarsMode(value);
    setPref('debug.cssVarsMode', value);
  };

  const handleFooterTheme = (value) => {
    setTheme(value);
    applyTheme(value);
    // Scope rule (Josh 2026-08-11): update the current DB's override when one
    // exists, otherwise write the global row.
    const slug = (localStorage.getItem('pmd-selected-db-slug') || '').toLowerCase();
    const dbId = slugToDbId[slug];
    const hasDbRow = dbId != null && prefRows.some(r => r.prefKey === 'display.theme' && r.databaseInstanceId === dbId);
    setPref('display.theme', value, hasDbRow ? 'db' : 'global');
    window.dispatchEvent(new Event('dawn-theme-changed'));
  };

  // Get status and notify from notification context
  const { status, notify } = useNotifications();

  // Dynamic version from rev log API — uses the lightweight AllowAnonymous
  // /current-version endpoint so non-SysAdmin footers show the version
  // (a20.046 — was silently blank for AppAdmin+ under real Entra auth).
  const [appVersion, setAppVersion] = useState('');
  const fetchVersion = () => {
    devFilesApi.getCurrentVersion()
      .then(res => setAppVersion(res.data?.currentVersion || ''))
      .catch(() => setAppVersion(''));
  };
  useEffect(() => {
    fetchVersion();
    window.addEventListener('pmd-version-updated', fetchVersion);
    return () => window.removeEventListener('pmd-version-updated', fetchVersion);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Database context (may be null on admin/dev pages)
  const dbContext = useDatabaseSafe();
  const dbDisplayName = dbContext?.dbDisplayName || '';
  // Always know the database name — from context or localStorage
  const databaseName = dbContext?.currentDb?.databaseName || dbContext?.currentDb?.id || localStorage.getItem('pmd-selected-database') || '';
  const isPreprod = databaseName.toLowerCase().includes('preprod');
  const badgeLabel = dbDisplayName || 'Admin';

  // Check if current page is a dev page
  const isDevPage = DEV_PAGES.includes(currentPage);
  const helpContent = PAGE_HELP[currentPage];

  const getEnteredTimestamp = () => {
    const now = new Date();
    const yy = String(now.getFullYear()).slice(-2);
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const hh = String(now.getHours()).padStart(2, '0');
    const mi = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');
    return `${yy}${mm}${dd}-${hh}${mi}${ss}`;
  };

  const getSourcePage = () => {
    // Convert URL path to readable page name
    const page = currentPage || 'home';
    return page.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const handleAddTask = async () => {
    if (!newTask.task.trim()) return;

    try {
      setSaving(true);

      // Create task directly via API (server assigns ID)
      await devFilesApi.createTask({
        task: newTask.task.trim(),
        status: 'pending',
        details: newTask.details.trim(),
        source: getSourcePage(),
        owner: null,
        claudeSummary: null
      });

      // Reset form
      setNewTask({ task: '', details: '' });
      setShowAddTask(false);
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to add task');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenModal = () => {
    setNewTask({ task: '', details: '' });
    // error cleared
    setShowAddTask(true);
  };

  return (
    <>
      <footer className="stui-footer">
        <div className="stui-footer-left">
          <span
            className={`stui-footer-db-badge ${isPreprod ? 'stui-footer-db-badge-preprod' : ''}`}
            title={databaseName || 'Administration'}
          >
            <Database size={12} />
            <span>{badgeLabel}</span>
          </span>
          {appVersion && <span className={`stui-footer-version ${isPreprod ? 'stui-footer-version-preprod' : ''}`}><ScrollText size={12} />{appVersion}</span>}
          {status && <span className="stui-footer-status">{status}</span>}
        </div>
        <div className="stui-footer-center" />
        <div className="stui-footer-right">
          {/* Debug panel button — visible when any debug tool is active */}
          {debugActive && (
            <div className="stui-debug-wrap" ref={debugPanelRef}>
              {debugOpen && (
                <div className="stui-debug-panel">
                  <div className="stui-debug-head">
                    <span className="stui-debug-title">Debug Tools</span>
                    <span className="stui-debug-hint">Shift+F12</span>
                    <button className="stui-debug-close" onClick={() => setDebugOpen(false)}><X size={12} /></button>
                  </div>
                  <div className="stui-debug-section">Hover Inspectors</div>
                  {DEBUG_HOVER_TOOLS.map(t => {
                    const on = !!toolStates[t.lsKey];
                    return (
                      <React.Fragment key={t.key}>
                        <div className="stui-debug-row" onClick={() => handleDebugToggle(t.key, t.lsKey, 'hover', !on)}>
                          <span className="stui-debug-label">{t.label}</span>
                          <span className={`stui-debug-tog${on ? ' on' : ''}`} />
                        </div>
                        {t.key === 'cssVars' && (
                          <div className="stui-debug-row stui-debug-mode-row">
                            <span className="stui-debug-label" style={{ color: 'var(--color-gray-400)', fontSize: '10px' }}>Var mode:</span>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button className={`stui-debug-mode-btn${cssVarsMode === 'used' ? ' on' : ''}`} onClick={e => { e.stopPropagation(); handleCssVarsMode('used'); }}>Used</button>
                              <button className={`stui-debug-mode-btn${cssVarsMode === 'scope' ? ' on' : ''}`} onClick={e => { e.stopPropagation(); handleCssVarsMode('scope'); }}>In Scope</button>
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                  <div className="stui-debug-section">Page Overlays</div>
                  {DEBUG_OVERLAY_TOOLS.map(t => {
                    const on = !!toolStates[t.lsKey];
                    return (
                      <div key={t.key} className="stui-debug-row" onClick={() => handleDebugToggle(t.key, t.lsKey, 'overlay', !on)}>
                        <span className="stui-debug-label">{t.label}</span>
                        <span className={`stui-debug-tog${on ? ' on' : ''}`} />
                      </div>
                    );
                  })}
                  <div className="stui-debug-section">Theme</div>
                  <div className="stui-debug-mode-row" style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {THEME_OPTIONS.map(t => (
                      <button key={t.value} className={`stui-debug-mode-btn${theme === t.value ? ' on' : ''}`} onClick={() => handleFooterTheme(t.value)}>{t.label}</button>
                    ))}
                  </div>
                </div>
              )}
              <button
                className={`stui-debug-btn${debugOpen ? ' active' : ''}`}
                onClick={() => setDebugOpen(o => !o)}
                title="Debug Tools (Shift+F12)"
              >
                <Bug size={13} />
                {debugOpen ? <ChevronDown size={10} /> : <ChevronUp size={10} />}
              </button>
            </div>
          )}
          {isSysAdmin && (
            <button
              className="stui-footer-add-task-btn"
              onClick={handleOpenModal}
              title="Add task to Current Sprint"
            >
              <ClipboardPlus size={16} />
            </button>
          )}
          {/* Help button - only on dev pages (will expand to all pages later) */}
          {isDevPage && helpContent && (
            <button
              className="stui-footer-help-btn"
              onClick={() => setShowHelp(true)}
              title="How this page works"
            >
              <span className="stui-footer-help-qmark">?</span>
            </button>
          )}
        </div>
      </footer>

      {/* Add Task Modal */}
      {showAddTask && (
        <div className="stui-modal-overlay">
          <div className="stui-modal">
            <div className="stui-modal-header">
              <h3>Add Sprint Task</h3>
              <button className="stui-modal-close" onClick={() => setShowAddTask(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="stui-modal-body">
              <div className="stui-form-row-inline">
                <div className="stui-form-field">
                  <label>Source Page</label>
                  <input
                    type="text"
                    className="stui-field-input"
                    value={getSourcePage()}
                    readOnly
                    disabled
                  />
                </div>
                <div className="stui-form-field">
                  <label>Entered</label>
                  <input
                    type="text"
                    className="stui-field-input"
                    value={getEnteredTimestamp()}
                    readOnly
                    disabled
                  />
                </div>
                <div className="stui-form-field">
                  <label>Status</label>
                  <input
                    type="text"
                    className="stui-field-input"
                    value="Pending"
                    readOnly
                    disabled
                  />
                </div>
              </div>
              <div className="stui-form-row">
                <label>Task Description</label>
                <input
                  type="text"
                  className="stui-field-input"
                  value={newTask.task}
                  onChange={(e) => setNewTask({ ...newTask, task: e.target.value })}
                  placeholder="Short description of the task"
                  autoFocus
                />
              </div>
              <div className="stui-form-row">
                <label>Task Details</label>
                <textarea
                  className="stui-field-input stui-field-textarea"
                  value={newTask.details}
                  onChange={(e) => setNewTask({ ...newTask, details: e.target.value })}
                  placeholder="Detailed requirements, context, or notes..."
                  rows={5}
                />
              </div>
            </div>
            <div className="stui-modal-footer">
              <button
                className="stui-btn stui-btn-secondary"
                onClick={() => setShowAddTask(false)}
              >
                Cancel
              </button>
              <button
                className="stui-btn stui-btn-primary"
                onClick={handleAddTask}
                disabled={saving || !newTask.task.trim()}
              >
                {saving ? 'Adding...' : 'Add Task'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Help Modal */}
      {showHelp && helpContent && (
        <div className="stui-modal-overlay">
          <div className="stui-modal">
            <div className="stui-modal-header">
              <h3>{helpContent.title}</h3>
              <button className="stui-modal-close" onClick={() => setShowHelp(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="stui-modal-body stui-help-sections">
              {helpContent.sections?.map((section, sIdx) => (
                <div key={sIdx} className="stui-help-section">
                  <h4 className="stui-help-section-heading">{section.heading}</h4>
                  <ul className="stui-help-section-list">
                    {section.items.map((item, iIdx) => (
                      <li key={iIdx}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
              {/* Fallback for old content format */}
              {!helpContent.sections && helpContent.content && (
                <ol className="stui-help-steps">
                  {helpContent.content.map((step, index) => (
                    <li key={index}>{step}</li>
                  ))}
                </ol>
              )}
            </div>
            <div className="stui-modal-footer">
              <button
                className="stui-btn stui-btn-primary"
                onClick={() => setShowHelp(false)}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Footer;
